import React, { useEffect, useState } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import Index from "./Routes";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AuthService } from "./services/dataService";
import { setTokens } from "./store/Slices/auth/authSlice";
import { setUserRole } from "./store/Slices/auth/authoriseSlice";
import IdleDetector from "./utils/IdleDetector";
import loadingGif from "./assets/Untitled design.gif";
import { SocketProvider } from "./components/Hooks/Useshocket";
import { SocketProvide } from "./components/Hooks/SocketProvider";
import LoginExpirePopup from "./components/RenewalPopup";
import ScreenSizeProvider from "./context/ScreenSizeProvider";
import DomainAssignPopupHandler from "./components/DomainAssignInfoPopup";
import GlobalEducationPopup from "./components/GlobalEducationPopup";
import { BASE_URL } from "./services/configUrls";
import GetHRinto from "./assets/Bringhrinto.png";
import EduSummitPopup from "./components/EduSummitPopup";
import TechCampHostingSurveyPopup from "./components/TechCampHostingSurveyPopup";
import AIFloatingButton from "./components/Chatbot/AIFloatingButton";
import AIChatbotModal from "./components/Chatbot/AIChatbotModal";
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "axios";


// ── HR Interest Popup Component ───────────────────────────
function HRInterestPopup({ open, onClose, onYes, onNo, loading }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ width: "100%", lineHeight: 0, flexShrink: 0 }}>
          <img
            src={GetHRinto}
            alt="Get HR Into Campus"
            style={{
              width: "100%",
              height: "610px",
              display: "block",
              objectFit: "cover",
              objectPosition: "top",
            }}
          />
        </Box>

        <Box display="flex" gap={1.5} justifyContent="center" sx={{ p: 2, flexShrink: 0, marginBottom: 12 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            onClick={onNo}
            sx={{
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              borderColor: "#e5e7eb",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: "0.8rem",
              "&:hover": { borderColor: "#d1d5db", bgcolor: "#f9fafb" },
            }}
          >
            Not Interested
          </Button>

          <Button
            variant="contained"
            size="small"
            disabled={loading}
            onClick={onYes}
            sx={{
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              background: "linear-gradient(135deg, #1d4ed8 0%, #4a90e2 100%)",
              fontWeight: 600,
              fontSize: "0.8rem",
              boxShadow: "0 4px 14px rgba(29,78,216,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)" },
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              "Yes, I'm Interested! 🚀"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}


// ── Allowed roles (HR popup, Summit, TechCamp) ────────────
const ALLOWED_ROLES = ["spoc", "leaders", "dspoc"];

// ── Chatbot allowed roles ─────────────────────────────────
const CHATBOT_ALLOWED_ROLES = ["spoc", "dspoc", "leader", "tpo", "educator"];


// ── Main App ──────────────────────────────────────────────
function App() {
  const [theme, colorMode] = useMode();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading]                   = useState(true);
  const [isChatOpen, setIsChatOpen]                 = useState(false);
  const [showGlobalEduPopup, setShowGlobalEduPopup] = useState(false);

  const [showHRPopup, setShowHRPopup] = useState(false);
  const [hrLoading, setHrLoading]     = useState(false);

  const [showSummitPopup, setShowSummitPopup]       = useState(false);
  const [showTechCampSurvey, setShowTechCampSurvey] = useState(false);


  // ── Step 1: Token init ────────────────────────────────────
  useEffect(() => {
    const initialize = async () => {
      const query        = new URLSearchParams(window.location.search);
      const accessToken  = query.get("accessToken");
      const refreshToken = query.get("refreshToken");
      const email        = query.get("email");

      if (accessToken && refreshToken) {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("userName", email);
        dispatch(setTokens({ accessToken, refreshToken }));
        window.history.replaceState({}, document.title, window.location.pathname);

        try {
          const rolesResponse = await AuthService.roles();
          const fetchedRoles  = rolesResponse.data.roles;
          const activeRole    = fetchedRoles.find((role) => role.status === true);
          if (activeRole) {
            const activeRoleNameModified = activeRole.role_name
              .toLowerCase()
              .replace(/ /g, "_");
            localStorage.setItem("Authorise", activeRoleNameModified);
            dispatch(setUserRole(activeRoleNameModified));
            navigate("/dashboard");
          }
        } catch (error) {
          console.log("Error fetching roles:", error);
        }
      }

      setIsLoading(false);
    };

    initialize();
  }, [navigate, dispatch]);


  // ── Step 2: Show GlobalEduPopup if authenticated ──────────
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      setShowGlobalEduPopup(true);
    }
  }, []);


  // ── Step 3: Check HR interest ─────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const role  = localStorage.getItem("Authorise");
    if (!token) return;
    if (!ALLOWED_ROLES.includes(role)) return;

    const checkHRInterest = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/hr-campus/interest`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { is_interested } = res.data;
        if (is_interested === null || is_interested === undefined) {
          setShowHRPopup(true);
        }
      } catch (err) {
        console.error("HR interest check failed:", err);
        setShowHRPopup(true);
      }
    };

    checkHRInterest();
  }, []);


  // ── Step 4: Show Summit popup ONLY if not already submitted ──
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const role  = localStorage.getItem("Authorise");
    if (!token) return;
    if (!ALLOWED_ROLES.includes(role)) return;

    const checkSummitStatus = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/edu-summit-survey/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.data.has_submitted) {
          setShowSummitPopup(true);
        }
      } catch (err) {
        setShowSummitPopup(true);
      }
    };

    checkSummitStatus();
  }, []);


  // ── Step 5: TechCamp Hosting Survey ──────────────────────
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const role  = localStorage.getItem("Authorise");
    if (!token) return;
    if (!ALLOWED_ROLES.includes(role)) return;

    const checkTechCampSurveyStatus = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/tech-camp-hosting-survey/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.data.has_submitted) {
          setShowTechCampSurvey(true);
        }
      } catch (err) {
        setShowTechCampSurvey(true);
      }
    };

    checkTechCampSurveyStatus();
  }, []);


  // ── HR: Yes ───────────────────────────────────────────────
  const handleHRYes = async () => {
    const token = localStorage.getItem("accessToken");
    setHrLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/api/hr-campus/interest`,
        { is_interested: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowHRPopup(false);
      navigate("/tech-camp-calendar", { state: { activeTab: 5 } });
    } catch (err) {
      console.error("Failed to save HR interest:", err);
    } finally {
      setHrLoading(false);
    }
  };


  // ── HR: No ────────────────────────────────────────────────
  const handleHRNo = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      await axios.post(
        `${BASE_URL}/api/hr-campus/interest`,
        { is_interested: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error("Failed to save HR interest:", err);
    } finally {
      setShowHRPopup(false);
    }
  };


  // ── Summit close ──────────────────────────────────────────
  const handleSummitClose = () => setShowSummitPopup(false);


  const isAuthenticated = !!localStorage.getItem("accessToken");
  const bearerToken     = localStorage.getItem("accessToken");
  const userRole        = localStorage.getItem("Authorise");

  // ── Chatbot visibility guard ──────────────────────────────
  const showChatbot = isAuthenticated && CHATBOT_ALLOWED_ROLES.includes(userRole);


  if (isLoading) {
    return (
      <div className="min-h-[100vh] justify-center flex items-center">
        <img
          src={loadingGif}
          alt="Loading..."
          className="w-[30%] h-auto object-cover"
        />
      </div>
    );
  }


  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isAuthenticated ? (
          <ScreenSizeProvider>
            <SocketProvider>
              <SocketProvide>
                <IdleDetector idleTimeout={600000}>
                  <Index />
                  <LoginExpirePopup />
                  <DomainAssignPopupHandler />

                  {/* Global Education Popup */}
                  {/* <GlobalEducationPopup
                    isOpen={showGlobalEduPopup}
                    onClose={() => setShowGlobalEduPopup(false)}
                    bearerToken={bearerToken}
                    userRole={userRole}
                  /> */}

                  {/* HR Interest Popup */}
                  <HRInterestPopup
                    open={showHRPopup}
                    onClose={() => setShowHRPopup(false)}
                    onYes={handleHRYes}
                    onNo={handleHRNo}
                    loading={hrLoading}
                  />

                  {/* EduSkills Summit Survey Popup */}
                  {/* <EduSummitPopup
                    open={showSummitPopup}
                    onClose={handleSummitClose}
                    onYes={handleSummitClose}
                    onNo={handleSummitClose}
                    userRole={userRole}
                  /> */}

                  {/* TechCamp Hosting Survey Popup */}
                  {/* <TechCampHostingSurveyPopup
                    open={showTechCampSurvey}
                    onClose={() => setShowTechCampSurvey(false)}
                    userRole={userRole}
                  /> */}

                  {/* ── AI Chatbot (spoc, dspoc, leader, tpo, educator) ── */}
                  {/* {showChatbot && (
                    <>
                      <AIFloatingButton
                        onClick={() => setIsChatOpen(true)}
                        isChatOpen={isChatOpen}
                      />
                      <AIChatbotModal
                        open={isChatOpen}
                        onClose={() => setIsChatOpen(false)}
                        onRaiseTicket={() => navigate("/raise-ticket")}
                      />
                    </>
                  )} */}

                </IdleDetector>
              </SocketProvide>
            </SocketProvider>
          </ScreenSizeProvider>
        ) : (
          <Index />
        )}
        <ToastContainer />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}


export default App;